import {
  Config as IConfig,
  Mixpanel as IMixpanel,
  People as IPeople,
} from 'mixpanel-browser';
import { AppConfig, UserInfo } from 'pmn/types';
import initMixpanel from './mixpanelSnippet';

type MixpanelTraits = {
  $email: string;
};

type Event = {
  event: string;
  id?: string;
  properties?: Record<string, unknown>;
  traits?: Record<string, unknown>;
};

class MockMixpanel
  implements
    Pick<
      IMixpanel,
      | 'get_distinct_id'
      | 'identify'
      | 'init'
      | 'people'
      | 'set_config'
      | 'track_links'
      | 'track'
    > {
  tracked: Event[];
  people: IPeople;

  get_distinct_id() {
    return 'mxp-default';
  }

  identify(id: string) {
    this.tracked.push({ event: 'identify', id });
  }

  track(event: string, properties: Event['properties']) {
    this.tracked.push({ event, properties });
  }

  track_links(
    element: HTMLAnchorElement,
    event: Event['event'],
    properties: Event['properties'],
  ) {
    this.tracked.push({ event: `${event}-link`, properties });
  }

  constructor() {
    this.tracked = [];
    this.people = {
      set: (traits: MixpanelTraits) => {
        this.tracked.push({ event: 'people.set', traits });
      },
    } as IPeople;
  }

  init(): IMixpanel {
    throw new Error('Method not implemented.');
  }

  set_config(): void {
    throw new Error('Method not implemented.');
  }
}

const adapt = (traits: UserInfo['traits']): MixpanelTraits => {
  const { email } = traits || {};
  return { $email: email };
};

declare global {
  interface Window {
    mixpanel: IMixpanel | MockMixpanel;
  }
}

export default class Mixpanel {
  mixpanel!: IMixpanel;
  // eslint-disable-next-line class-methods-use-this
  init(
    { MIXPANEL_ENABLED, MIXPANEL_TOKEN }: AppConfig,
    onLoad: IConfig['loaded'],
  ): void {
    if (MIXPANEL_ENABLED) {
      initMixpanel(MIXPANEL_TOKEN);

      const { mixpanel } = window;
      // disable autotrack and historical page view tracking
      // we track pages manually instead
      mixpanel.init(MIXPANEL_TOKEN, {
        autotrack: false,
        debug: false,
        track_pageview: false,
        // required for cookies to appear on staging
        // might need to be disabled if we want it to work on givingfuel
        cross_subdomain_cookie: false,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        loaded: onLoad || function noop() {},
      });
    } else {
      const mixpanel = (new MockMixpanel() as unknown) as IMixpanel;
      window.mixpanel = mixpanel;
      onLoad(mixpanel);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  identify(userInfo: UserInfo): void {
    const { id, traits } = userInfo || {};
    window.mixpanel.identify(id);
    window.mixpanel.people.set(adapt(traits));
  }

  // eslint-disable-next-line class-methods-use-this
  track(event: string, properties: Event['properties']): void {
    window.mixpanel.track(event, properties);
  }

  // eslint-disable-next-line class-methods-use-this
  trackLink(
    element: HTMLAnchorElement,
    event: string,
    properties: Event['properties'],
  ): void {
    window.mixpanel.track_links(element, event, properties);
  }

  page(userInfo: UserInfo): void {
    const { traits } = userInfo || {};
    const { location } = window;

    const pageProperties = {
      title: document.title,
      url: location.toString(),
      path: location.pathname,
      search: location.search,
    };

    const properties = Object.assign({}, traits || {}, pageProperties);

    this.track('Loaded a Page', properties);
  }

  // eslint-disable-next-line class-methods-use-this
  getDistinctId(): string {
    return window.mixpanel.get_distinct_id();
  }
}
